import { Box } from "../../box";
import { cloneElement, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CrossButton } from "../../button";
import { Div } from "../../html";
import { cssModule, isNil, isString, mergeProps, normalizeSize, useAutoFocusChild, useEventCallback, useFocusManager, useFocusScope, useId, useMergedRefs, useRefState, useResizeObserver, useSlots } from "../../shared";
import { useResponsiveValue } from "../../styling";
import { Text } from "../../typography";
import { Underlay, useOverlayFocusRing, useRestoreFocus, useTrapFocus } from "../../overlay";
import { useDialogTriggerContext } from "./DialogTriggerContext";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DefaultElement = "section";

function useHideBodyScrollbar() {
  const [stateRef, setState] = useRefState({
    isVisible: true,
    originalPosition: ""
  });
  useEffect(() => {
    if (stateRef.current.isVisible) {
      setState({
        isVisible: false,
        originalPosition: document.body.style.overflowY
      });
      document.body.style.overflowY = "hidden";
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const state = stateRef.current;

      if (!state.isVisible) {
        setState({
          isVisible: true,
          originalPosition: ""
        });
        document.body.style.overflowY = state.originalPosition;
      }
    };
  }, [stateRef, setState]);
}

function useElementHasVerticalScrollbar() {
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const [elementRef, setElement] = useRefState();
  const handleElementResize = useEventCallback(() => {
    setHasScrollbar(elementRef.current.scrollHeight > elementRef.current.clientHeight);
  });
  const resizeRef = useResizeObserver(handleElementResize);
  return [useMergedRefs(setElement, resizeRef), hasScrollbar];
}

function isElementInViewport(element) {
  const clientRect = element.getBoundingClientRect();
  return clientRect.top >= 0 && clientRect.left >= 0 && clientRect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && clientRect.right <= (window.innerWidth || document.documentElement.clientWidth);
}

export function InnerDialog({
  "aria-describedby": ariaDescribedBy,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  as = DefaultElement,
  children,
  dismissable = true,
  focus,
  forwardedRef,
  id,
  role = "dialog",
  size,
  wrapperProps,
  zIndex = 1,
  ...rest
}) {
  var _heading$props, _content$props, _header$props, _header$props2, _footer$props, _footer$props2;

  const sizeValue = useResponsiveValue(size);
  const [focusScope, setFocusRef] = useFocusScope();
  const wrapperRef = useRef();
  const dialogRef = useMergedRefs(forwardedRef, setFocusRef);
  const dismissButtonRef = useRef();
  const {
    close
  } = useDialogTriggerContext();
  useHideBodyScrollbar();
  const [hasVerticalScrollbarRef, wrapperHasVerticalScrollbar] = useElementHasVerticalScrollbar();
  const focusManager = useFocusManager(focusScope);
  useTrapFocus(focusManager);
  const restoreFocusProps = useRestoreFocus(focusScope);
  useAutoFocusChild(focusManager, {
    canFocus: useCallback(element => {
      // Do not autofocus the dialog itself.
      if (element === dialogRef.current) {
        return false;
      } // Do not autofocus the dismiss button.


      if (element === dismissButtonRef.current) {
        return false;
      } // Do not autofocus a link.


      if ((element === null || element === void 0 ? void 0 : element.tagName) === "A") {
        return false;
      }

      if (!isElementInViewport(element)) {
        return false;
      }

      return true;
    }, [dialogRef, dismissButtonRef]),
    onNotFound: useEventCallback(() => {
      var _dialogRef$current;

      (_dialogRef$current = dialogRef.current) === null || _dialogRef$current === void 0 ? void 0 : _dialogRef$current.focus();
    })
  });
  const focusRingProps = useOverlayFocusRing({
    focus
  });
  const handleDismissButtonClick = useEventCallback(event => {
    if (!isNil(close)) {
      close(event);
    }
  });
  const dialogId = useId(id, "o-ui-dialog");
  const {
    button,
    "button-group": buttonGroup,
    content,
    footer,
    header,
    heading,
    illustration,
    image
  } = useSlots(children, useMemo(() => ({
    _: {
      required: ["heading", "content"]
    },
    button: {
      className: "o-ui-dialog-button"
    },
    "button-group": {
      className: "o-ui-dialog-button-group"
    },
    content: {
      as: Text,
      className: "o-ui-dialog-content",
      id: `${dialogId}-content`
    },
    footer: {
      as: "footer",
      className: "o-ui-dialog-footer"
    },
    header: {
      as: "header",
      className: "o-ui-dialog-header"
    },
    heading: {
      as: "h3",
      className: "o-ui-dialog-heading",
      id: `${dialogId}-heading`,
      size: "sm"
    },
    illustration: {
      className: "o-ui-dialog-illustration",
      orientation: "vertical"
    },
    image: null
  }), [dialogId]));
  const headingId = heading === null || heading === void 0 ? void 0 : (_heading$props = heading.props) === null || _heading$props === void 0 ? void 0 : _heading$props.id;
  const contentId = content === null || content === void 0 ? void 0 : (_content$props = content.props) === null || _content$props === void 0 ? void 0 : _content$props.id;

  const imageMarkup = image && /*#__PURE__*/_jsx(Div, {
    className: "o-ui-dialog-image",
    children: image
  });

  const headerMarkup = isString(header === null || header === void 0 ? void 0 : (_header$props = header.props) === null || _header$props === void 0 ? void 0 : _header$props.children) ? /*#__PURE__*/cloneElement(header, {
    children: /*#__PURE__*/_jsx(Text, {
      children: header === null || header === void 0 ? void 0 : (_header$props2 = header.props) === null || _header$props2 === void 0 ? void 0 : _header$props2.children
    })
  }) : header;
  const footerMarkup = isString(footer === null || footer === void 0 ? void 0 : (_footer$props = footer.props) === null || _footer$props === void 0 ? void 0 : _footer$props.children) ? /*#__PURE__*/cloneElement(footer, {
    children: /*#__PURE__*/_jsx(Text, {
      children: footer === null || footer === void 0 ? void 0 : (_footer$props2 = footer.props) === null || _footer$props2 === void 0 ? void 0 : _footer$props2.children
    })
  }) : footer;

  const dismissButtonMarkup = dismissable && /*#__PURE__*/_jsx(CrossButton, {
    "aria-label": "Dismiss",
    className: "o-ui-dialog-dismiss-button",
    condensed: true,
    onClick: handleDismissButtonClick,
    ref: dismissButtonRef,
    size: "xs"
  });

  const headerSectionMarkup = (!isNil(heading) || !isNil(headerMarkup)) && /*#__PURE__*/_jsxs(Div, {
    className: "o-ui-dialog-header-section",
    children: [heading, headerMarkup]
  });

  const footerSectionMarkup = (!isNil(footerMarkup) || !isNil(button) || !isNil(buttonGroup)) && /*#__PURE__*/_jsxs(Div, {
    className: "o-ui-dialog-footer-section",
    children: [footerMarkup, button, buttonGroup]
  });

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Underlay, {
      zIndex: zIndex
    }), /*#__PURE__*/_jsx(Div, { ...mergeProps(wrapperProps !== null && wrapperProps !== void 0 ? wrapperProps : {}, {
        className: cssModule("o-ui-dialog-wrapper", wrapperHasVerticalScrollbar && "scrolling"),
        ref: useMergedRefs(wrapperRef, hasVerticalScrollbarRef),
        zIndex: zIndex + 1
      }),
      children: /*#__PURE__*/_jsxs(Box, { ...mergeProps(rest, {
          "aria-describedby": !isNil(ariaDescribedBy) ? ariaDescribedBy : contentId !== null && contentId !== void 0 ? contentId : undefined,
          "aria-label": ariaLabel,
          "aria-labelledby": isNil(ariaLabel) ? ariaLabelledBy !== null && ariaLabelledBy !== void 0 ? ariaLabelledBy : headingId : undefined,
          "aria-modal": true,
          as,
          className: cssModule("o-ui-dialog", sizeValue === "fullscreen" ? sizeValue : normalizeSize(sizeValue), focus && "focus"),
          id: dialogId,
          ref: dialogRef,
          role,
          tabIndex: -1
        }, focusRingProps, restoreFocusProps),
        children: [dismissButtonMarkup, imageMarkup, illustration, /*#__PURE__*/_jsxs(Div, {
          className: "o-ui-dialog-aside",
          children: [headerSectionMarkup, content, footerSectionMarkup]
        })]
      })
    })]
  });
}
InnerDialog.defaultElement = DefaultElement;
export const Dialog = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerDialog, { ...props,
  forwardedRef: ref
}));